<template>
  <div class="account-form-wrap">
    <div class="settings-section">
      <div class="settings-section__header">
        <div class="section-icon section-icon--blue">
          <i class="el-icon-user-solid"></i>
        </div>
        <h3 class="settings-section__title">{{ $t('settings.sectionINFO') }}</h3>
      </div>

      <el-form
        class="account-edit-form"
        ref="form"
        :rules="rules"
        :model="internalAccount"
        label-position="top">

        <div class="form-grid">
          <el-form-item :label="$t('base.firstName')" prop="firstName">
            <el-input v-model="internalAccount.firstName" prefix-icon="el-icon-user"></el-input>
          </el-form-item>

          <el-form-item :label="$t('base.appLoginName')" prop="appLoginName">
            <el-input v-model="internalAccount.appLoginName" prefix-icon="el-icon-key"></el-input>
          </el-form-item>

          <el-form-item :label="$t('base.lastName')" prop="lastName">
            <el-input v-model="internalAccount.lastName" prefix-icon="el-icon-user"></el-input>
          </el-form-item>

          <el-form-item :label="$t('settings.authority')">
            <el-select
              v-model="internalAccount.userAuthority"
              :placeholder="$t('settings.authority')"
              style="width: 100%">
              <el-option
                v-for="authority in authorities"
                :label="authority"
                :key="authority"
                :value="authority">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item :label="$t('base.email')" prop="email">
            <el-input v-model="internalAccount.email" prefix-icon="el-icon-message"></el-input>
          </el-form-item>

          <el-form-item :label="$t('settings.active')" v-if="isEdit">
            <el-switch v-model="internalAccount.active"></el-switch>
          </el-form-item>
        </div>

        <div class="form-actions">
          <base-button
            v-if="!isEdit"
            type="success"
            :loading="loading"
            @click="onSubmit">
            <i class="el-icon-plus"></i> {{ $t('settings.create') }}
          </base-button>
          <base-button
            v-if="isEdit"
            type="primary"
            :loading="loading"
            @click="onSubmit">
            <i class="el-icon-check"></i> {{ $t('settings.save') }}
          </base-button>
          <base-button @click="onCancel">
            {{ $t('base.cancel') }}
          </base-button>
        </div>
      </el-form>
    </div>

    <div class="settings-section" v-if="isEdit">
      <div class="settings-section__header">
        <div class="section-icon section-icon--purple">
          <i class="el-icon-office-building"></i>
        </div>
        <h3 class="settings-section__title">{{ $t('settings.customer.assign') }}</h3>
      </div>

      <div class="assign-body">
        <div class="assign-row">
          <el-select
            v-model="selectedCustomer"
            filterable
            clearable
            class="assign-select"
            :placeholder="$t('settings.customer.selectToAssign')">
            <el-option
              v-for="customer in availableCustomers"
              :key="customer.id"
              :label="customer.name"
              :value="customer.id">
            </el-option>
          </el-select>
          <base-button
            type="primary"
            :disabled="!selectedCustomer"
            @click="assignCustomer">
            {{ $t('settings.customer.assignButton') }}
          </base-button>
        </div>

        <div
          class="assigned-tags"
          v-if="internalAccount.customers && internalAccount.customers.length > 0">
          <el-tag
            v-for="customer in internalAccount.customers"
            :key="customer.id"
            closable
            class="assigned-tag"
            @close="removeCustomer(customer)">
            {{ customer.name }}
          </el-tag>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import BaseButton from '@/components/common/BaseButton';

const tenantStateMappers = createNamespacedHelpers('tenant');

export default {
  name: "AccountForm",
  components: {BaseButton},
  props: {
    loading: {
      default: false
    },
    tenantId: {
      required: true
    },
    account: {
      default: null
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      internalAccount: {
        userAuthority: 'ADMIN',
        tenantId: this.tenantId,
        active: true,
        customers: []
      },
      selectedCustomer: null,
      authorities: ['SUPER_ADMIN', 'ADMIN', 'REPORTER', 'USER'],
      rules: {
        firstName: [
          { required: true, message: this.$t('settings.firstNameRequired'), trigger: 'blur' }
        ],
        lastName: [
          { required: true, message: this.$t('settings.lastNameRequired'), trigger: 'blur' }
        ],
        email: [
          { required: true, message: this.$t('settings.emailRequired'), trigger: 'blur' },
          { type: 'email', message: this.$t('settings.invalidEmail'), trigger: 'blur' }
        ],
        appLoginName: [
          { required: true, message: this.$t('settings.appLoginNameRequired'), trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...tenantStateMappers.mapGetters(['getAllCustomers', 'getTenantCustomers']),
    availableCustomers() {
      // Filter out customers that are already assigned
      if (this.internalAccount.customers) {
        const assignedIds = this.internalAccount.customers.map(c => c.id);
        return this.getTenantCustomers.filter(c => !assignedIds.includes(c.id));
      }
      return this.getTenantCustomers;
    }
  },
  watch: {
    account: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.internalAccount = {...newVal};
          if (!this.internalAccount.customers) {
            this.internalAccount.customers = [];
          }
        }
      }
    }
  },
  mounted() {
    // Load customers for the tenant if in edit mode
    if (this.isEdit) {
      this.$store.dispatch('tenant/getCustomersByTenant', this.tenantId);
    }
  },
  methods: {
    onSubmit() {
      this.$refs['form'].validate((valid) => {
        if (valid) {
          this.$emit('submit', this.internalAccount);
        }
      });
    },
    onCancel() {
      this.$emit('cancel');
    },
    assignCustomer() {
      if (this.selectedCustomer) {
        const customer = this.getTenantCustomers.find(c => c.id === this.selectedCustomer);
        if (customer) {
          // Add to the internal account's customers
          if (!this.internalAccount.customers) {
            this.internalAccount.customers = [];
          }
          this.internalAccount.customers.push(customer);

          // Clear selection
          this.selectedCustomer = null;

          // Emit assignment if in edit mode
          if (this.isEdit) {
            this.$store.dispatch('tenant/assignCustomerToAccount', {
              tenantId: this.tenantId,
              customerId: customer.id,
              accountId: this.internalAccount.id
            }).then(() => {
              this.$message.success(this.$t('settings.customer.assignSuccess'));
            }).catch(() => {
              this.$message.error(this.$t('settings.genericHttpError'));
              // Remove from local state if API call fails
              const index = this.internalAccount.customers.findIndex(c => c.id === customer.id);
              if (index !== -1) {
                this.internalAccount.customers.splice(index, 1);
              }
            });
          }
        }
      }
    },
    removeCustomer(customer) {
      const index = this.internalAccount.customers.findIndex(c => c.id === customer.id);
      if (index !== -1) {
        this.internalAccount.customers.splice(index, 1);

        // You could add API call here to remove customer from account if needed
        this.$message.success(this.$t('settings.customer.removedFromAccount'));
      }
    }
  }
}
</script>

<style scoped lang="scss">
.account-form-wrap {
  width: 100%;
  min-width: 0;
}

.section-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    color: #fff;
    font-size: 15px;
  }

  &--blue {
    background: linear-gradient(135deg, #409EFF, #2d7dd2);
  }

  &--purple {
    background: linear-gradient(135deg, #6366f1, #4f46e5);
  }
}

.settings-section {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .04);
  margin-bottom: 20px;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
    background: #f8f9fc;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }
}

.account-edit-form {
  padding: 24px 20px 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
}

:deep(.el-form-item__label) {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  padding-bottom: 6px;
  line-height: 1.4;
}

:deep(.el-input__inner) {
  border-radius: 8px;
  border-color: var(--border);
  height: 40px;
  font-size: 13.5px;
  color: var(--text-primary);
  transition: border-color .2s, box-shadow .2s;

  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(64, 158, 255, .12);
  }
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}

.form-actions {
  display: flex;
  gap: 10px;
  padding-top: 4px;
}

.assign-body {
  padding: 20px;
}

.assign-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.assign-select {
  flex: 1;
}

.assigned-tags {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.assigned-tag {
  border-radius: 20px;
  padding: 0 12px;
}
</style>
